import type { ChartPoint } from '@/admin/dashboards/chartViewModel';

export const SPARKLINE_HEIGHT_PX = 48;
export const SPARKLINE_SCORE_STROKE_TOP = '#60A5FA';
export const SPARKLINE_SCORE_STROKE_BOTTOM = '#2563EB';
export const SPARKLINE_SCORE_FILL = '#3B82F6';
export const SPARKLINE_DOTTED_TAIL = '#D0D5DD';
export const SPARKLINE_DOTTED_DASHARRAY = '2 3';
export const SPARKLINE_RELEASE_VIOLET = '#8B5CF6';

/**
 * Y domain for a health sparkline.
 * The axis is at least as tall as the series magnitude, and at least twice
 * the data span, centered on the midpoint. A move from 8 to 10 then uses a
 * fraction of the height instead of filling it.
 */
export function sparklineYDomain(values: readonly number[]): [number, number] {
  const finite = values.filter((value) => Number.isFinite(value));
  if (finite.length === 0) return [0, 1];
  const low = Math.min(...finite);
  const high = Math.max(...finite);
  const mid = (low + high) / 2;
  const dataSpan = high - low;
  const magnitude = Math.max(Math.abs(low), Math.abs(high));
  const axisSpan = Math.max(dataSpan * 2, magnitude, 1);
  const half = axisSpan / 2;
  return [mid - half, mid + half];
}

export type SparklineRow = {
  x: string;
  value: number | null;
  n: number | null;
  solidValue: number | null;
  dottedValue: number | null;
};

/** First weekly index where the API marks tooFew (0 < n < 30), or null. */
export function firstTooFewWeekIndex(weekly: ChartPoint[]): number | null {
  for (let i = 0; i < weekly.length; i += 1) {
    if (weekly[i].tooFew === true) return i;
  }
  return null;
}

/**
 * Splits values into a solid monotone segment and a dashed too-few tail.
 * The dotted series begins at the last solid week so the two strokes meet continuously.
 */
export function buildSparklineSegmentRows(weekly: ChartPoint[]): SparklineRow[] {
  const firstTooFew = firstTooFewWeekIndex(weekly);
  return weekly.map((point, index) => {
    const value = point.value ?? null;
    const base = {
      x: String(point.x),
      value,
      n: point.n ?? null,
    };
    if (firstTooFew === null) {
      return { ...base, solidValue: value, dottedValue: null };
    }
    if (firstTooFew === 0) {
      return { ...base, solidValue: null, dottedValue: value };
    }
    if (index < firstTooFew) {
      const dottedValue = index === firstTooFew - 1 ? value : null;
      return { ...base, solidValue: value, dottedValue };
    }
    return { ...base, solidValue: null, dottedValue: value };
  });
}
